import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CalendarIcon, ReceiptText, FileText } from 'lucide-react';
import { Client } from '@/types/client';
import { formatDate } from '@/utils/date-utils';
import { useState, useEffect } from 'react';
import { getRecentMealPlans } from '@/lib/apiService';

interface OverviewTabContentProps {
  client: Client;
}

interface RecentMealPlan {
  id: string;
  name: string;
  date: string;
  status: string;
  planType?: string;
  description: string;
  creationMethod?: string;
  planStatus?: string; // Original status from API (draft, finalized, etc.)
}

// Helper function to format creation method
const getCreationMethodLabel = (method?: string): string => {
  if (!method) return '';
  switch (method) {
    case 'manual':
      return 'Manual';
    case 'auto_generated':
      return 'Auto';
    case 'ai_generated':
    case 'ai_assisted':
      return 'AI';
    default:
      return method.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

// Helper function to get creation method badge color
const getCreationMethodBadgeColor = (method?: string): string => {
  if (!method) return 'bg-gray-50 text-gray-700 border-gray-200';
  switch (method) {
    case 'manual':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'auto_generated':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'ai_generated':
    case 'ai_assisted':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

// Helper function to generate the correct URL based on status and creation method
const manualPlanPath = (planId: string, clientId?: string) =>
  clientId
    ? `/manual-meal-plan/${planId}/client/${clientId}`
    : '/manual-meal-plan';

const getMealPlanUrl = (
  planId: string,
  status: string,
  clientId: string,
  creationMethod?: string,
  planType?: string,
): string => {
  const normalizedStatus = status?.toLowerCase?.() ?? '';
  const normalizedCreationMethod = creationMethod?.toLowerCase?.();
  const normalizedPlanType = planType?.toLowerCase?.();
  const isDraftLikeStatus = ['draft', 'in_progress', 'active', 'pending'].includes(normalizedStatus);

  // If API marks it finalized, always open finalized view regardless of status text ("active", etc.).
  if (normalizedPlanType === 'finalized') {
    return clientId
      ? `/finalized-meal-plan/${planId}/client/${clientId}`
      : '/meal-planning';
  }

  // Legacy fallback when planType is missing.
  if (normalizedStatus === 'finalized' || normalizedStatus === 'completed') {
    return clientId
      ? `/finalized-meal-plan/${planId}/client/${clientId}`
      : '/meal-planning';
  }
  
  // For draft or draft-like statuses, route based on creation method
  if (isDraftLikeStatus || planId.startsWith('manual-') || normalizedCreationMethod) {
    const methodToUse =
      normalizedCreationMethod ||
      (planId.startsWith('manual-') ? 'manual' : undefined);

    switch (methodToUse) {
      case 'manual':
        return manualPlanPath(planId, clientId);
      case 'ai_generated':
      case 'ai_assisted':
        return clientId
          ? `/ai-meal-plan/draft/${planId}/client/${clientId}`
          : '/ai-meal-planning';
      case 'auto_generated':
        return clientId
          ? `/automated-meal-plan/draft/${planId}/client/${clientId}`
          : '/auto-meal-planning';
      default:
        if (planId.startsWith('manual-')) {
          return manualPlanPath(planId, clientId);
        }
        break;
    }
  }
  
  // Default fallback
  return clientId ? `/clients/${clientId}/details-v2/meal-plan` : '/meal-planning';
};

export const OverviewTabContent: React.FC<OverviewTabContentProps> = ({ client }) => {
  const [recentMealPlans, setRecentMealPlans] = useState<RecentMealPlan[]>([]);
  const [isLoadingMealPlans, setIsLoadingMealPlans] = useState(true);
  const [mealPlansError, setMealPlansError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentMealPlans = async () => {
      if (!client?.id) return;
      
      try {
        setIsLoadingMealPlans(true);
        setMealPlansError(null);
        
        const response = await getRecentMealPlans(client.id);
        
        if (response && response.success && response.data && response.data.plans) {
          // Transform API response to match component structure
          const plans = response.data.plans.map((plan: any) => {
            const raw = plan.creationMethod ?? plan.creationType ?? plan.generationType;
            const creationMethod = raw === 'automated' || raw === 'auto' ? 'auto_generated'
              : raw === 'ai_generated' || raw === 'ai_assisted' || raw === 'ai' ? 'ai_generated'
              : raw === 'manual' ? 'manual'
              : raw;
            return {
            id: plan.id,
            name: plan.name || plan.planName || 'Untitled Meal Plan',
            date: plan.createdAt 
              ? formatDate(new Date(plan.createdAt))
              : plan.planDate
              ? formatDate(new Date(plan.planDate))
              : 'N/A',
            status: plan.status || 'draft',
            planType: plan.planType,
            creationMethod,
            planStatus: plan.status, // Store original status for routing
            description: `A ${plan.durationDays || 1}-day meal plan${plan.summary?.completionPercentage !== undefined ? ` - ${plan.summary.completionPercentage}% complete` : ''}.`
          };
          });
          
          setRecentMealPlans(plans);
        } else {
          setRecentMealPlans([]);
        }
      } catch (error) {
        console.error('Error fetching recent meal plans:', error);
        setMealPlansError('Failed to load recent meal plans');
        setRecentMealPlans([]);
      } finally {
        setIsLoadingMealPlans(false);
      }
    };

    fetchRecentMealPlans();
  }, [client?.id]);

  return (
    <div className="space-y-6">
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-muted/20">
        <CardContent className="pt-6 pb-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Appointments Card */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-primary/5">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-lg font-bold text-foreground">
                    <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                      <CalendarIcon className="h-4 w-4 text-primary" />
                    </div>
                    Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Last Appointment</p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate(client.lastInteractionAt ? new Date(client.lastInteractionAt) : null)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Next Appointment</p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate((client as any).nextInteractionAt ? new Date((client as any).nextInteractionAt) : null)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Card */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-secondary/5">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-lg font-bold text-foreground">
                    <div className="h-8 w-8 bg-secondary/10 rounded-full flex items-center justify-center mr-3">
                      <ReceiptText className="h-4 w-4 text-secondary" />
                    </div>
                    Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Subscription Start</p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate((client as any).subscriptionStartAt ? new Date((client as any).subscriptionStartAt) : null)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Subscription End</p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate((client as any).subscriptionEndAt ? new Date((client as any).subscriptionEndAt) : null)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Next Invoice</p>
                    <p className="text-sm font-medium text-foreground">
                      {formatDate((client as any).nextInvoiceDate ? new Date((client as any).nextInvoiceDate) : null)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Recent Meal Plans Card */}
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-accent/5">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-xl font-bold text-foreground">
            <div className="flex items-center">
              <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              Recent Meal Plans
            </div>
            <Button variant="outline" size="default" asChild className="border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300">
              <Link to={`/clients/${client.id}/details-v2/meal-plan`}>
                View All
              </Link>
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoadingMealPlans ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading recent meal plans...</div>
            </div>
          ) : mealPlansError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-destructive">{mealPlansError}</div>
            </div>
          ) : recentMealPlans.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">No recent meal plans found.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {recentMealPlans.map((plan) => (
                <div key={plan.id} className="p-5 border border-border/30 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-muted/10 hover:border-primary/30">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-base text-foreground break-words leading-tight">{plan.name}</h4>
                    <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-md whitespace-nowrap ml-2">{plan.date}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed break-words">{plan.description}</p>
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                        plan.status === 'finalized'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : plan.status === 'draft'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : plan.status === 'completed'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'bg-gray-50 text-gray-700 border border-gray-200'
                      }`}>
                        {plan.status || 'unknown'}
                      </span>
                      {plan.creationMethod && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getCreationMethodBadgeColor(plan.creationMethod)}`}>
                          {getCreationMethodLabel(plan.creationMethod)}
                        </span>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-sm font-medium hover:bg-primary/10 hover:text-primary transition-colors p-2 flex-shrink-0"
                      onClick={() => {
                        const url = getMealPlanUrl(
                          plan.id,
                          plan.planStatus || 'draft',
                          client.id,
                          plan.creationMethod,
                          plan.planType,
                        );
                        console.log('Draft ID:', plan.id);
                        console.log('Client ID:', client.id);
                        navigate(url, { state: { from: location.pathname + location.search } });
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
